import { Button } from '@components/core-ui/form-elements/buttons/Button/';
import { OutlinedButton } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { Input } from '@components/core-ui/form-elements/Input';
import { PaymentMethod } from '@modals/Billing/TopUpCreditBalance/components/PaymentMethod';
import { Plans } from '@modals/Billing/TopUpCreditBalance/components/Plans';
import { AbsoluteSpinner } from '@components/core-ui/spinners/Spinner';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { formatPrice } from '@helpers/formatPrice';
import { useAPI } from '@providers/useAPI';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useMutation, useQuery } from '@tanstack/react-query';
import { PaymentService } from 'app/API';
import { ApiCallError } from 'app/http/ApiCallError';
import { billingKeys } from 'app/queries/billing/billingKeys';
import { createFetchMemberBillingAccountQueryFn } from 'app/queries/billing/createFetchMemberBillingAccountQueryFn';
import { createFetchMemberPaymentMethodsQueryFn } from 'app/queries/billing/createFetchMemberPaymentMethodsQueryFn';
import React, { useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ModalTitle } from '@modals/core-ui/ModalTitle';
import { ModalWrapper } from '@modals/core-ui/ModalWrapper';
import { getTopUpLoadErrorMessage } from './helpers/getTopUpLoadErrorMessage';
import { Content } from './styles/Content';
import { TProps } from './types/TProps';

export const TopUpCreditBalanceModal = ({ billingAccountId, onCancel, onSuccess, initialAmount = 10 }: TProps) => {
  const { closeModal, openModal } = useModalContext();
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const baId = billingAccountId || organization?.billingAccount.id || '';
  let initAmount = initialAmount;
  if (initAmount < 10) {
    initAmount = 10;
  }
  const [amount, setAmount] = useState(initAmount);
  const billingAccountQuery = useQuery({
    queryKey: billingKeys.memberAccount(baId),
    queryFn: createFetchMemberBillingAccountQueryFn(call),
  });
  const paymentMethodsQuery = useQuery({
    queryKey: billingKeys.memberPaymentMethodsByAccount(baId),
    queryFn: createFetchMemberPaymentMethodsQueryFn(call),
    enabled: billingAccountQuery.isSuccess,
  });
  const billingAccount = billingAccountQuery.data;
  const [rate, setRate] = useState(parseFloat((billingAccount?.tierRate ?? 0).toString()));
  const [promoRate, setPromoRate] = useState(0);
  const [resultAmount, setResultAmount] = useState((billingAccount?.currentBalance ?? 0) / 100 + 10);
  const paymentMethods = paymentMethodsQuery.data
    ? {
        default: paymentMethodsQuery.data.find((item) => item.default) || paymentMethodsQuery.data[0],
        all: paymentMethodsQuery.data,
      }
    : undefined;
  const requestErrorRef = useRef<{ message: string } | null>(null);

  const onlyXLeftPosition = ((amount - 10) / 2570) * 100;
  function calculateNextTier() {
    if (rate < 11) {
      return { nextTierLeft: 0, nextTierName: '', showOnlyXLeft: false };
    }
    if (rate > 10 && resultAmount > 1750) {
      return { nextTierLeft: Math.ceil(2000 - resultAmount), nextTierName: 'Business', showOnlyXLeft: true };
    }
    if (rate > 11 && resultAmount > 750) {
      return { nextTierLeft: Math.ceil(1000 - resultAmount), nextTierName: 'Agency', showOnlyXLeft: true };
    }
    if (rate > 12 && resultAmount > 400) {
      return { nextTierLeft: Math.ceil(500 - resultAmount), nextTierName: 'Pro', showOnlyXLeft: true };
    }
    if (rate > 13.5 && resultAmount > 50) {
      return { nextTierLeft: Math.ceil(100 - resultAmount), nextTierName: 'Freelancer', showOnlyXLeft: true };
    }
    return { nextTierLeft: 0, nextTierName: '', showOnlyXLeft: false };
  }
  const { nextTierLeft, nextTierName, showOnlyXLeft } = calculateNextTier();

  const loadError = billingAccountQuery.error ?? paymentMethodsQuery.error;
  useEffect(() => {
    if (!loadError) {
      return;
    }
    const message = getTopUpLoadErrorMessage(loadError);
    if (message) {
      toast.error(message);
    }
  }, [loadError]);

  useEffect(() => {
    if (!billingAccount) {
      return;
    }
    const defaultRate = parseFloat((billingAccount?.tierRate ?? 0).toString());
    const currentBalance = (billingAccount.currentBalance ?? 0) / 100;
    const newAmount = amount + currentBalance;

    // If fixed rate is set
    if (billingAccount?.fixedRate) {
      setRate(defaultRate);
      setPromoRate(0);
      setResultAmount(newAmount);
      return;
    }

    // Otherwise, calculate the rate based on the amount
    let newRate = defaultRate;
    if (newAmount >= 2000) {
      newRate = 10;
    } else if (newAmount >= 1000) {
      newRate = 11;
    } else if (newAmount >= 500) {
      newRate = 12;
    } else if (newAmount >= 100) {
      newRate = 13.5;
    }
    setResultAmount(newAmount);
    setRate(newRate);
  }, [amount, billingAccount]);

  const topUpMutation = useMutation({
    mutationFn: () => {
      requestErrorRef.current = null;
      return call(
        () =>
          PaymentService.createPaymentIntent({
            baId,
            requestBody: {
              amount: amount * 100,
              balanceTopup: true,
            },
          }),
        {
          onError: (message) => {
            requestErrorRef.current = { message };
          },
        },
      );
    },
    onSuccess: (result) => {
      if (['succeeded', 'processing'].includes(result.status)) {
        const latestCharge: unknown = result.latest_charge;
        openModal(ModalNamesEnum.TransactionStatus, {
          billingAccountId: baId,
          chargeId: typeof latestCharge === 'string' && latestCharge.length > 0 ? latestCharge : undefined,
          paymentIntentId: result.id,
          pics: result.client_secret,
          onSuccess,
        });
        return;
      }
      if (result.status === 'requires_action' && result.next_action?.type === 'redirect_to_url') {
        window.location.href = (result.next_action?.redirect_to_url as { url: string }).url;
        return;
      }
      if ((result.status ?? '').includes('requires_')) {
        openModal(ModalNamesEnum.Status, {
          title: 'Payment Status',
          message: (
            <>
              Something went wrong. Please try again later <br /> (Status code: {result.status})
            </>
          ),
          status: 'error',
          buttonMessage: 'Ok',
          onSuccess,
        });
        return;
      }
      closeModal();
      onSuccess();
    },
    onError: (error) => {
      const requestError = requestErrorRef.current;
      if (requestError) {
        openModal(ModalNamesEnum.Status, {
          title: 'Payment Status',
          message: (
            <>
              Something went wrong. Please try again later <br /> Error: {requestError.message}
            </>
          ),
          status: 'error',
          buttonMessage: 'Ok',
          onSuccess,
        });
        return;
      }
      if (!(error instanceof ApiCallError)) {
        toast.error('Unable to complete the top-up. Please try again.');
      }
    },
  });

  const isLoading = billingAccountQuery.isLoading || paymentMethodsQuery.isLoading || topUpMutation.isPending;

  const isSubmitDisabled = isLoading || !paymentMethods?.default || !Number.isFinite(amount) || amount < 10;

  const renderAmountLine = () => {
    return (
      <div className="amount-line">
        <div className="input-wrapper">
          <div className="label">Amount (USD) *</div>
          <Input
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!e.target.value.length) {
                setAmount(0);
                return;
              }
              const nextAmount = parseInt(e.target.value, 10);
              if (!Number.isFinite(nextAmount)) {
                return;
              }
              setAmount(nextAmount);
            }}
            label="Amount"
            value={amount}
          />
        </div>
        <div className="input-wrapper">
          <div className="label">Balance after top-up</div>
          <div className="balance">{formatPrice(resultAmount)}</div>
        </div>
        <div className="input-wrapper">
          <div className="label">Estimated audio hours</div>
          <div className="balance">
            {promoRate && rate ? (
              <span>{Math.floor(resultAmount / rate)}</span>
            ) : rate ? (
              Math.floor(resultAmount / rate)
            ) : (
              'Loading...'
            )}
          </div>
        </div>
        <div className="input-wrapper new-rate">
          <div className="label">Your new rate</div>
          <div className="balance">
            {promoRate && rate ? (
              <span>{`$${rate.toFixed(2).toString()}`}</span>
            ) : rate ? (
              `$${rate.toFixed(2).toString()}`
            ) : (
              'Loading...'
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <ModalWrapper width="780px" overflow="visible">
      {isLoading ? <AbsoluteSpinner overlay={true} /> : null}
      <ModalTitle>Prepaid Credit Balance</ModalTitle>

      <Content>
        <div className="input-wrapper">
          <div className="label">Payment method</div>
          <PaymentMethod defaultPaymentMethod={paymentMethods?.default} isLoading={isLoading} />
        </div>
        <div className="input-wrapper">
          <div className="label">Rate</div>
          <Plans
            amount={amount}
            billingAccount={billingAccount}
            nextTierLeft={nextTierLeft}
            nextTierName={nextTierName}
            onlyXLeftPosition={onlyXLeftPosition}
            rate={rate}
            setAmount={setAmount}
            showOnlyXLeft={showOnlyXLeft}
          />
        </div>

        {renderAmountLine()}

        <div className="description">* Note: minimum top up amount is $10</div>
        <div className="description">** Balance updates automatically shortly after the payment completes</div>

        <div className="buttonGroup">
          <OutlinedButton onClick={onCancel}>Cancel</OutlinedButton>
          <Button disabled={isSubmitDisabled} onClick={() => topUpMutation.mutate()}>
            Pay now
          </Button>
        </div>
      </Content>
    </ModalWrapper>
  );
};
